import { useState, type ChangeEvent } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import type { Pedido } from "../../types/pedido";
import { subirEvidenciaReapertura } from "../../lib/storage";
import { crearReaperturaConTrazabilidad, crearSolicitudReaperturaSinTrazabilidad } from "../../services/pruebasService";

function hoyISODate(): string {
  return new Date().toISOString().slice(0, 10);
}

interface ReabrirPedidoModalProps {
  open: boolean;
  onClose: () => void;
  pedido: Pedido;
  userName: string;
  onReabierto: () => void;
}

export function ReabrirPedidoModal({ open, onClose, pedido, userName, onReabierto }: ReabrirPedidoModalProps) {
  const [sufijo, setSufijo] = useState("");
  const [fechaEmision, setFechaEmision] = useState(hoyISODate());
  const [fechaEntrega, setFechaEntrega] = useState(hoyISODate());
  const [evidenciaFile, setEvidenciaFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const esCasoA = sufijo.trim() !== "";
  const nuevoNumero = `${pedido.numeroPedido}${sufijo.trim()}`;

  const reset = () => {
    setSufijo("");
    setFechaEmision(hoyISODate());
    setFechaEntrega(hoyISODate());
    setEvidenciaFile(null);
    setError(null);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const handleEvidenciaChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEvidenciaFile(e.target.files?.[0] || null);
    setError(null);
  };

  const handleSubmit = async () => {
    setError(null);
    if (esCasoA && !evidenciaFile) {
      setError("La evidencia es obligatoria para reabrir con trazabilidad.");
      return;
    }
    setLoading(true);
    try {
      const fechaEmisionISO = new Date(fechaEmision).toISOString();
      const fechaEntregaISO = new Date(fechaEntrega).toISOString();

      if (esCasoA) {
        const evidenciaUrl = await subirEvidenciaReapertura(evidenciaFile as File, pedido.numeroPedido, pedido.id);
        await crearReaperturaConTrazabilidad({
          pedidoId: pedido.id,
          origenNumeroNuevo: nuevoNumero,
          fechaEmision: fechaEmisionISO,
          fechaEntrega: fechaEntregaISO,
          evidenciaUrl,
          solicitadoPor: userName,
        });
        toast.success(`Pedido reabierto con trazabilidad: ${nuevoNumero}`);
        reset();
        onReabierto();
      } else {
        await crearSolicitudReaperturaSinTrazabilidad({
          pedidoId: pedido.id,
          fechaEmisionNueva: fechaEmisionISO,
          fechaEntregaNueva: fechaEntregaISO,
          solicitadoPor: userName,
        });
        toast.success("Solicitud de reapertura enviada. Queda pendiente de autorización.");
        reset();
        onClose();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("Error al reabrir pedido:", err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reabrir Pedido</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">No. de Pedido</label>
              <Input value={pedido.numeroPedido} disabled readOnly />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sufijo (nuevo número)</label>
              <Input
                value={sufijo}
                onChange={e => setSufijo(e.target.value)}
                placeholder="ej. -R1"
              />
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            {esCasoA
              ? <>Se creará un pedido <strong>nuevo</strong> con número <strong>{nuevoNumero}</strong>, copia completa del actual. El pedido original no se modifica.</>
              : "Se reabrirá el mismo pedido, sin cambiar el número. Requiere autorización antes de aplicarse."}
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Fecha de emisión</label>
              <Input type="date" value={fechaEmision} onChange={e => setFechaEmision(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Fecha de entrega</label>
              <Input type="date" value={fechaEntrega} onChange={e => setFechaEntrega(e.target.value)} />
            </div>
          </div>

          {esCasoA && (
            <div>
              <label className="block text-sm font-medium mb-1">Evidencia de reapertura</label>
              <Input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={handleEvidenciaChange} />
            </div>
          )}

          {error && <p className="text-red-600 text-sm">{error}</p>}
        </div>
        <DialogFooter>
          <div className="flex justify-end gap-4">
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? "Procesando..." : esCasoA ? "Reabrir con trazabilidad" : "Solicitar reapertura"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
